import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const RESET_DURATION = 30 * 1000; // 30 seconds

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [remainingTime, setRemainingTime] = useState(0);

    // Countdown
    useEffect(() => {
        if (remainingTime <= 0) return; 

        const timer = setInterval(() => {
            setRemainingTime((prev) => {
                if (prev <= 1000) {
                    clearInterval(timer);
                    return 0;
                }

                return prev - 1000;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [remainingTime]);

    const formatTime = (milliseconds) => {
        const totalSeconds = Math.ceil(milliseconds / 1000);

        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
            2,
            "0"
        )}`;
    };

    const startTimer = () => {
        setRemainingTime(RESET_DURATION);
    };

    const sendResetLink = async () => {
        if (!email.trim()) {
            toast.error("Please enter your email address.");
            return;
        }

        setLoading(true);

        try {

            const apiUrl = "https://consult.conversationaily.ai";
            const response = await axios.post(
                `${apiUrl}/api/users/forgot-password`,
                {
                    email: email.trim(),
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                response.data?.message ||
                "Password reset link has been sent to your email."
            );

            // Start 30 seconds timer
            startTimer();
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                "Unable to process your request. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        await sendResetLink();
    };

    const handleResend = async () => {
        await sendResetLink();
    };

    const isTimerActive = remainingTime > 0;

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">

                {/* Logo */}
                <div className="text-center">
                    <img
                        src="./logo.png"
                        alt="Company Logo"
                        className="mx-auto mb-6 h-20 w-52 object-contain"
                    />

                    <h2 className="text-2xl font-bold text-gray-800">
                        Forgot Password?
                    </h2>

                    <p className="mt-2 text-sm text-gray-500">
                        Enter your registered email address and we'll send you a
                        password reset link.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="mt-8">

                    {/* Email */}
                    <div>
                        <label
                            htmlFor="email"
                            className="mb-2 block text-sm font-medium text-gray-700"
                        >
                            Email Address
                        </label>

                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            required
                        />
                    </div>

                    {/* Timer */}
                    {isTimerActive && (
                        <div className="mt-4 rounded-lg bg-blue-50 p-4 text-center">
                            <p className="text-sm text-gray-600">
                                You can resend the reset link in
                            </p>

                            <p className="mt-1 text-2xl font-bold text-blue-600">
                                {formatTime(remainingTime)}
                            </p>
                        </div>
                    )}

                    {/* Send / Resend Button */}
                    {!isTimerActive ? (
                        <button
                            type={remainingTime === 0 ? "submit" : "button"}
                            onClick={!remainingTime ? handleResend : undefined}
                            disabled={loading}
                            className="mt-6 w-full rounded-lg bg-orange-400 px-4 py-3 text-center text-sm font-semibold text-white shadow-md transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loading
                                ? "Sending..."
                                : remainingTime === 0
                                    ? "Send Reset Link"
                                    : "Resend Reset Link"}
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled
                            className="mt-6 w-full cursor-not-allowed rounded-lg bg-gray-300 px-4 py-3 text-center text-sm font-semibold text-gray-600"
                        >
                            Resend available in {formatTime(remainingTime)}
                        </button>
                    )}

                    {/* Back to Login */}
                    <div className="mt-6 text-center">
                        <Link
                            to="/"
                            className="text-sm font-medium text-blue-600 hover:underline"
                        >
                            ← Back to Login
                        </Link>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;